import React, { useCallback, useEffect, useRef } from 'react';
import { resolveGameIconUrl } from '../../../utils/gameHelpers';
import './categorySlider.css';

/**
 * Horizontal category strip: arrows + auto-advance.
 * Mobile shows 2 items; desktop shows more via CSS.
 */
export default function CategorySlider({
  items = [],
  selectedIndex = -1,
  onSelect,
  selectable = false,
  showLabels = true,
  autoMs = 3500,
  ariaLabel = 'Categories',
}) {
  const trackRef = useRef(null);
  const pauseUntilRef = useRef(0);

  const scrollByPage = useCallback((dir) => {
    const el = trackRef.current;
    if (!el) return;
    const step = el.clientWidth;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    if (max <= 4) return;
    let next;
    if (dir > 0 && el.scrollLeft >= max - 4) next = 0;
    else if (dir < 0 && el.scrollLeft <= 4) next = max;
    else next = Math.min(max, Math.max(0, el.scrollLeft + dir * step));
    el.scrollTo({ left: next, behavior: 'smooth' });
  }, []);

  const pauseAuto = useCallback(() => {
    pauseUntilRef.current = Date.now() + autoMs * 2;
  }, [autoMs]);

  const go = useCallback(
    (dir) => {
      pauseAuto();
      scrollByPage(dir);
    },
    [pauseAuto, scrollByPage]
  );

  useEffect(() => {
    if (items.length <= 5 || !autoMs) return undefined;
    const id = setInterval(() => {
      if (Date.now() < pauseUntilRef.current) return;
      scrollByPage(1);
    }, autoMs);
    return () => clearInterval(id);
  }, [items.length, autoMs, scrollByPage]);

  if (!items.length) return null;

  const showArrows = items.length > 5;

  return (
    <div
      className={`cat-slider${showArrows ? '' : ' cat-slider--no-arrows'}`}
      role="region"
      aria-label={ariaLabel}
    >
      {showArrows ? (
        <button type="button" className="cat-slider-arrow" aria-label="Previous" onClick={() => go(-1)}>
          &#8592;
        </button>
      ) : null}

      <div
        className="cat-slider-track"
        ref={trackRef}
        onPointerDown={pauseAuto}
        onTouchStart={pauseAuto}
      >
        {items.map((item, i) => {
          const src = resolveGameIconUrl(item.icon || item.image);
          const selected = selectable && selectedIndex === i;
          const Tag = selectable ? 'button' : 'div';
          return (
            <Tag
              key={item.key || item.name || i}
              type={selectable ? 'button' : undefined}
              className={`cat-slider-item${selected ? ' is-selected' : ''}${
                selectable ? ' is-selectable' : ''
              }`}
              onClick={selectable && onSelect ? () => onSelect(i) : undefined}
              title={item.name}
              aria-pressed={selectable ? selected : undefined}
            >
              {src ? (
                <img src={src} alt={item.name || ''} referrerPolicy="no-referrer" />
              ) : (
                <span className="cat-slider-fallback">{item.name}</span>
              )}
              {showLabels && item.name ? (
                <span className="cat-slider-label">{item.name}</span>
              ) : null}
            </Tag>
          );
        })}
      </div>

      {showArrows ? (
        <button type="button" className="cat-slider-arrow" aria-label="Next" onClick={() => go(1)}>
          &#8594;
        </button>
      ) : null}
    </div>
  );
}
